import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { httpsCallable, functions } from '@/lib/functions';
import type { Booking, FlightStatus, FlightNotificationPreferences, StatusUnavailableReason } from './bookings';

const getFlightStatusCallable = httpsCallable(functions, 'getFlightStatusFunction');
const updatePreferencesCallable = httpsCallable(functions, 'updateFlightNotificationPreferencesFunction');
const getCheckInLinkCallable = httpsCallable(functions, 'getCheckInLinkFunction');

/**
 * Fetch latest flight status from Cloud Function
 */
export const fetchFlightStatus = async (
  bookingId: string,
  tripId: string,
  options: { forceRefresh?: boolean } = {}
): Promise<{
  status: FlightStatus | null;
  statusUnavailableReason?: StatusUnavailableReason;
  flightNumber?: string;
  airlineCode?: string;
  gate?: string;
  terminal?: string;
  checkInLink?: string;
  checkInAvailable?: boolean;
  /** ISO strings from the callable (Firestore timestamps serialized). */
  lastUpdated?: string;
  isStale?: boolean;
  nextRefreshAt?: string;
}> => {
  try {
    const result = await getFlightStatusCallable({
      bookingId,
      tripId,
      forceRefresh: options.forceRefresh ?? false,
    });
    return result.data as {
      status: FlightStatus | null;
      statusUnavailableReason?: StatusUnavailableReason;
      flightNumber?: string;
      airlineCode?: string;
      gate?: string;
      terminal?: string;
      checkInLink?: string;
      checkInAvailable?: boolean;
      lastUpdated?: string;
      isStale?: boolean;
      nextRefreshAt?: string;
    };
  } catch (error: any) {
    console.error('Error fetching flight status:', error);
    throw error;
  }
};

export const refreshFlightStatus = async (bookingId: string, tripId: string) => {
  return fetchFlightStatus(bookingId, tripId, { forceRefresh: true });
};

/** Fallback check-in URLs when backend fails or is not configured */
const FALLBACK_CHECKIN_URLS: Record<string, string> = {
  UA: 'https://www.united.com/en/us/checkin',
  UAL: 'https://www.united.com/en/us/checkin',
  AA: 'https://www.aa.com/check-in',
  AAL: 'https://www.aa.com/check-in',
  DL: 'https://www.delta.com/flight-status/check-in',
  DAL: 'https://www.delta.com/flight-status/check-in',
  WN: 'https://www.southwest.com/check-in/',
  SWA: 'https://www.southwest.com/check-in/',
  B6: 'https://www.jetblue.com/check-in',
  AS: 'https://www.alaskaair.com/planbook/check-in',
};

export function getFallbackCheckInUrl(airlineCode: string | undefined): string | null {
  if (!airlineCode || !airlineCode.trim()) return null;
  const code = airlineCode.toUpperCase().trim();
  return FALLBACK_CHECKIN_URLS[code] ?? null;
}

export const fetchCheckInLink = async (bookingId: string, tripId: string): Promise<string | null> => {
  try {
    const result = await getCheckInLinkCallable({ bookingId, tripId });
    return (result.data as { checkInLink?: string }).checkInLink ?? null;
  } catch (error: any) {
    console.error('Error fetching check-in link:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time flight status updates for a booking
 */
export const subscribeToFlightStatus = (
  tripId: string,
  bookingId: string,
  callback: (booking: Booking | null) => void
): Unsubscribe => {
  const bookingRef = doc(db, 'trips', tripId, 'bookings', bookingId);
  
  return onSnapshot(
    bookingRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      const data = snapshot.data();
      const booking: Booking = {
        id: snapshot.id,
        type: data.type as Booking['type'],
        provider: data.provider,
        confirmationNumber: data.confirmationNumber,
        checkInDate: data.checkInDate?.toDate(),
        checkOutDate: data.checkOutDate?.toDate(),
        departureDate: data.departureDate?.toDate(),
        arrivalDate: data.arrivalDate?.toDate(),
        location: data.location,
        address: data.address,
        amount: data.amount,
        currency: data.currency,
        rawEmailSubject: data.rawEmailSubject,
        rawEmailDate: data.rawEmailDate?.toDate(),
        status: data.status || 'confirmed',
        tripId: data.tripId,
        userId: data.userId,
        createdAt: data.createdAt?.toDate() || new Date(),
        syncedAt: data.syncedAt?.toDate() || new Date(),
        flightStatus: data.flightStatus,
        checkInLink: data.checkInLink,
        checkInAvailable: data.checkInAvailable,
        notificationPreferences: data.notificationPreferences,
        flightNumber: data.flightNumber,
        airlineCode: data.airlineCode,
        lastStatusCheck: data.lastStatusCheck?.toDate(),
      };

      callback(booking);
    },
    (error) => {
      console.error('Error in flight status subscription:', error);
      callback(null);
    }
  );
};

/**
 * Update notification preferences for a flight booking
 */
export const updateNotificationPreferences = async (
  bookingId: string,
  tripId: string,
  preferences: FlightNotificationPreferences
): Promise<void> => {
  try {
    await updatePreferencesCallable({ bookingId, tripId, preferences });
  } catch (error: any) {
    console.error('Error updating notification preferences:', error);
    throw error;
  }
};

/**
 * Get status badge color based on flight status
 */
export const getStatusColor = (status?: FlightStatus['status']): string => {
  switch (status) {
    case 'on-time':
      return '#10B981'; // green
    case 'delayed':
      return '#F59E0B'; // amber
    case 'cancelled':
      return '#EF4444'; // red
    case 'scheduled':
      return '#3B82F6'; // blue
    case 'unknown':
    default:
      return '#6B7280'; // gray
  }
};

/**
 * Get status label
 */
export const getStatusLabel = (status?: FlightStatus['status']): string => {
  switch (status) {
    case 'on-time':
      return 'On Time';
    case 'delayed':
      return 'Delayed';
    case 'cancelled':
      return 'Cancelled';
    case 'scheduled':
      return 'Scheduled';
    case 'unknown':
    default:
      return 'Unknown';
  }
};

/**
 * Format delay duration
 */
export const formatDelay = (delayMinutes?: number): string | null => {
  if (!delayMinutes || delayMinutes <= 0) return null;
  
  if (delayMinutes >= 60) {
    const hours = Math.floor(delayMinutes / 60);
    const minutes = delayMinutes % 60;
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  
  return `${delayMinutes}m`;
};

/**
 * Check if check-in is available (typically 24 hours before departure)
 */
export const isCheckInAvailable = (booking: Booking): boolean => {
  if (booking.checkInAvailable) return true;
  if (!booking.departureDate) return false;
  
  const now = new Date();
  const departure = booking.departureDate;
  const hoursUntilDeparture = (departure.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  // Check-in typically opens 24 hours before departure
  return hoursUntilDeparture >= 0 && hoursUntilDeparture <= 48;
};

